import { Box, Flex, IconButton, Image, Switch, Table, Tbody, Td, Th, Thead, Tr, useDisclosure } from '@chakra-ui/react';
import { flexRender, getCoreRowModel, getSortedRowModel, useReactTable, createColumnHelper } from '@tanstack/react-table';
import { useMemo, useState } from 'react';
import { DeleteIcon, EditIcon } from '@chakra-ui/icons';
import { BiCommentDetail } from 'react-icons/bi';
import isEmpty from 'lodash/isEmpty';
import { useHistory } from 'react-router-dom';
import { formatDate } from 'utils/helpers';
import { FORMAT_DATE, ROOT_API } from 'constants/common';
import { toast } from 'components/Toast';
import {
  useChangeStatusProductMutation,
  useChangeStatusSoldoutMutation,
  useDeleteProductMutation,
  useSetHotProductMutation,
  useSetNewProductMutation,
} from 'services/product';
import DetailProductModal from './DetailProductModal';
import AuthorizationComponentWrapper from 'components/AuthorizationComponentWrapper/AuthorizationComponentWrapper';
import { useUserState } from 'context/UserContext';

const ProductTable = ({ productsData, refetch }) => {
  const history = useHistory();
  const [sorting, setSorting] = useState([]);
  const [productDetail, setProductDetail] = useState();
  const columnHelper = createColumnHelper();
  const { userInfo } = useUserState();

  const deleteProductMutation = useDeleteProductMutation();
  const changeStatusProductMutation = useChangeStatusProductMutation();
  const setNewProductMutation = useSetNewProductMutation();
  const setHotProductMutation = useSetHotProductMutation();
  const changeStatusSoldoutProductMutation = useChangeStatusSoldoutMutation();
  const { isOpen, onOpen, onClose } = useDisclosure();

  const handleDeleteSize = product => {
    const confirmDelete = window.confirm('Bạn có chắc chắn muốn xóa sản phẩm này không?');
    if (!confirmDelete) {
      return;
    }

    deleteProductMutation.mutate(
      { id: product?._id },
      {
        onSuccess: () => {
          toast.showMessageSuccess('Xóa sản phẩm thành công');
          refetch?.();
        },
        onError: () => {
          toast.showMessageError('Xóa sản phẩm thất bại');
          refetch?.();
        },
      }
    );
  };

  const onActionProduct = (productId, title, actionApi) => {
    actionApi.mutate(
      { id: productId },
      {
        onSuccess: () => {
          toast.showMessageSuccess(`${title} thành công`);
          refetch?.();
        },
        onError: () => {
          toast.showMessageError(`${title} thất bại`);
          refetch?.();
        },
      }
    );
  };

  const columns = useMemo(
    () => [
      columnHelper.accessor('thumbnails', {
        header: 'Ảnh',
        cell: info => (
          <Box minW="100px" minH="100px">
            <Image src={info.getValue()[0]?.includes('https') ? info.getValue()[0] : ROOT_API + '/' + info.getValue()[0]} boxSize="100px" />
          </Box>
        ),
      }),
      columnHelper.accessor('name', {
        header: 'Tên',
        cell: info => <Box textTransform="capitalize">{info.renderValue()}</Box>,
      }),
      columnHelper.accessor('price', {
        header: 'Coin quy đổi',
        cell: info => info.renderValue(),
      }),
      columnHelper.accessor('exchangedQuantity', {
        header: 'Số lượng đã đổi',
        cell: info => info.renderValue(),
      }),
      columnHelper.accessor('remainingQuantity', {
        header: 'Số lượng còn lại',
        cell: info => info.renderValue(),
      }),
      columnHelper.accessor('totalQuantity', {
        header: 'Tổng số lượng',
        cell: info => info.renderValue(),
      }),
      columnHelper.accessor('categoryId', {
        header: 'Danh mục',
        cell: info => <Box>{info.getValue()?.title}</Box>,
      }),
      columnHelper.accessor('isBest', {
        header: 'Nổi bật',
        cell: info => (
          <Switch
            size="md"
            isChecked={info.getValue()}
            onChange={e => onActionProduct(info?.row?.original?._id, 'Thay đổi trạng thái sản phẩm nổi bật', setHotProductMutation)}
          />
        ),
      }),
      columnHelper.accessor('isPublished', {
        header: 'Phát hành',
        cell: info => (
          <Switch
            size="md"
            isChecked={info.getValue()}
            onChange={e => onActionProduct(info?.row?.original?._id, 'Thay đổi trạng thái phát hành sản phẩm', changeStatusProductMutation)}
          />
        ),
      }),
      columnHelper.accessor('isSoldOut', {
        header: 'Hết sản phẩm',
        cell: info => (
          <Switch
            size="md"
            isChecked={info.getValue()}
            onChange={e =>
              onActionProduct(info?.row?.original?._id, 'Thay đổi trạng thái sản phẩm hết sản phẩm', changeStatusSoldoutProductMutation)
            }
          />
        ),
      }),
      // columnHelper.accessor('isNew', {
      //   header: 'Mới',
      //   cell: info => (
      //     <Switch
      //       size="md"
      //       isChecked={info.getValue()}
      //       onChange={e => onActionProduct(info?.row?.original?._id, 'Thiết lập sản phẩm mới', setNewProductMutation)}
      //     />
      //   ),
      // }),
      columnHelper.accessor('createdAt', {
        header: 'Ngày tạo',
        cell: info => formatDate(info.getValue(), FORMAT_DATE),
      }),
      columnHelper.accessor('action', {
        header: 'Thao tác',
        cell: info => (
          <Flex alignItems="center" gap={1}>
            <AuthorizationComponentWrapper groupPermission={userInfo?.permissions} groupName={'VIEW_DETAIL_PRODUCT'}>
              <IconButton
                bg="transparent"
                onClick={() => {
                  setProductDetail(info?.row?.original);
                  onOpen();
                }}
              >
                <BiCommentDetail cursor="pointer" boxSize={4} />
              </IconButton>
            </AuthorizationComponentWrapper>
            <AuthorizationComponentWrapper groupPermission={userInfo?.permissions} groupName={'UPDATE_PRODUCT'}>
              <IconButton
                bg="transparent"
                onClick={() => {
                  history.push(`/admin/product/${info?.row?.original?._id}`);
                }}
              >
                <EditIcon cursor="pointer" boxSize={4} />
              </IconButton>
              <IconButton
                bg="transparent"
                onClick={() => {
                  handleDeleteSize(info?.row?.original);
                }}
              >
                <DeleteIcon color="red.400" boxSize={4} />
              </IconButton>
            </AuthorizationComponentWrapper>
          </Flex>
        ),
      }),
    ],
    [productsData]
  );

  const table = useReactTable({
    data: productsData || [],
    columns,
    state: {
      sorting,
    },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    debugTable: true,
  });

  return (
    <Table>
      <Thead>
        {table.getHeaderGroups().map(headerGroup => (
          <Tr key={headerGroup.id}>
            {headerGroup.headers.map(header => (
              <Th key={header.id} w="120px" isTruncated>
                {header.isPlaceholder ? null : (
                  <Box
                    cursor={header.column.getCanSort() ? 'pointer' : 'default'}
                    onClick={header.column.getToggleSortingHandler()}
                    isTruncated
                  >
                    {flexRender(header.column.columnDef.header, header.getContext())}
                    {{
                      asc: ' 🔼',
                      desc: ' 🔽',
                    }[header.column.getIsSorted()] ?? null}
                  </Box>
                )}
              </Th>
            ))}
          </Tr>
        ))}
      </Thead>
      <Tbody>
        {isEmpty(table.getRowModel().rows) ? (
          <Tr>
            <Td textAlign="center" colSpan={10}>
              Không có dữ liệu
            </Td>
          </Tr>
        ) : (
          table.getRowModel().rows.map(row => (
            <Tr key={row.id}>
              {row.getVisibleCells().map(cell => (
                <Td key={cell.id} isTruncated>
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </Td>
              ))}
            </Tr>
          ))
        )}
      </Tbody>
      <DetailProductModal isOpen={isOpen} productDetail={productDetail} onClose={onClose} />
    </Table>
  );
};

export default ProductTable;
